<!-- components/online/PlayerReports.vue -->
<script setup>
import { ref, onMounted, computed } from 'vue';
import { usePlayerStore } from '../../stores/player/playerStore';
import { useReportsStore } from '../../stores/reports/reportsStore';
import { useAuthStore } from '../../stores/auth';
import { useI18n } from 'vue-i18n'

const { t } = useI18n();
const playerStore = usePlayerStore();
const reportsStore = useReportsStore();
const authStore = useAuthStore();

// Estado para las pestañas
const activeTab = ref('transactions');

// Cargar reportes al cambiar a esa pestaña
const changeTab = (tab) => {
  activeTab.value = tab;
  // Configurar fechas por defecto (último mes)
  const today = new Date();
  const lastMonth = new Date();
  lastMonth.setDate(today.getDate() - 30);
  
  // En lugar de usar updateFilters, asigna directamente los valores
  reportsStore.filters.fromDate = lastMonth.toISOString().split('T')[0];
  reportsStore.filters.toDate = today.toISOString().split('T')[0];
  reportsStore.filters.type = 'all';
  reportsStore.filters.game = '';
  reportsStore.filters.bonusType = null;
  reportsStore.filters.status = null;
  
  if (tab === 'transactions') {
    reportsStore.fetchCashReports();
  } else if (tab === 'games') {
    reportsStore.fetchGameStats();
  } else if (tab === 'bonuses') {
    reportsStore.fetchBonusStats();
  }
};


const changePage = (page) => {
  reportsStore.filters.page = page;
  
  if (activeTab.value === 'transactions') {
    reportsStore.fetchCashReports();
  } else if (activeTab.value === 'games') {
    reportsStore.fetchGameStats();
  }
};

// Computed properties
const currency_prefix = computed(() => playerStore.currencyPrefix)
const formattedBalance = computed(() => {
  return Number(playerStore.balance).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
})
const formattedBonusBalance = computed(() => playerStore.formattedBonusBalance);
const user = computed(() => authStore.user);
const transactionsData = computed(() => reportsStore.formattedTransactions);
const stats = computed(() => reportsStore.cashReports.stats || {});
const isLoading = computed(() => reportsStore.isCashReportsLoading);
// Propiedades computadas para estadísticas de juego - corregido
const gameHistory = computed(() => reportsStore.gameStats.game_history?.data || []);
const gameHistoryPagination = computed(() => reportsStore.gameStats.game_history || { current_page: 1, last_page: 1 });
const gameTotals = computed(() => reportsStore.gameStats.stats || {});
const isGameStatsLoading = computed(() => reportsStore.isGameStatsLoading);
// Añade estas propiedades computadas a tu componente
const cashbackHistory = computed(() => reportsStore.cashbackHistory || []);
const jackpotHistory = computed(() => reportsStore.jackpotHistory || []);
const bonusStatusStats = computed(() => reportsStore.bonusStatusStats || []);
// const bonusTypeStats = computed(() => reportsStore.bonusTypeStats || []);
const bonusTotals = computed(() => reportsStore.bonusTotals || {});
const isBonusStatsLoading = computed(() => reportsStore.isBonusStatsLoading);



// Método para aplicar filtros
const applyFilters = () => {
  if (activeTab.value === 'transactions') {
    reportsStore.fetchCashReports();
  } else if (activeTab.value === 'games') {
    reportsStore.fetchGameStats();
  }
};


// Formatear fecha
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  return date.toLocaleDateString() + ' ' + 
         date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
};



onMounted(() => {
  // Inicializar escuchas de tiempo real
  reportsStore.initializeRealtimeUpdates();
  // Cargar datos iniciales
  changeTab('transactions');
});
</script>

<template>
  <div class="player-reports-modal">
    <!-- Cabecera del modal -->
    <div class="modal-header">
      <h2> {{ t('report.tittle') }}</h2>
      <button class="close-modal-btn" @click="$emit('close')">×</button>
    </div>
    
    <!-- Información del jugador -->
    <div class="player-info">
      <div class="info-item">
        <span class="info-label">{{ t('report.user') }} : </span>
        <span class="info-value">{{ user?.username || 'player' }}</span>
      </div>
      <div class="info-item">
        <span class="info-label">{{ t('account.balance') }} : </span>
        <span class="info-value">{{ currency_prefix }} {{ formattedBalance }}</span>
      </div>
      <div class="info-item">
        <span class="info-label">{{ t('account.bonus') }} :</span>
        <span class="info-value">{{ currency_prefix }} {{ formattedBonusBalance }}</span>
      </div>
    </div>
    
    <!-- Pestañas de navegación -->
<div class="reports-tabs">
  <button 
    :class="['tab-btn', { active: activeTab === 'transactions' }]"
    @click="changeTab('transactions')"
  >
    {{ t('report.transactions') }}
  </button>
  <button 
    :class="['tab-btn', { active: activeTab === 'games' }]"
    @click="changeTab('games')"
  >
    {{ t('report.games') }}
  </button>
  <button 
    :class="['tab-btn', { active: activeTab === 'bonuses' }]"
    @click="changeTab('bonuses')"
  >
    {{ t('report.bonuses') }}
  </button>
</div>

    
    <!-- Contenido de la pestaña de transacciones -->
    <div v-if="activeTab === 'transactions'" class="tab-content">
      <!-- Contenido existente para transacciones... -->
      <!-- Filtros -->
      <div class="filters">
        <div class="filter-group">
          <label>{{ t('report.from') }} : </label>
          <input 
            type="date" 
            v-model="reportsStore.filters.fromDate" 
            @change="applyFilters"
          >
        </div>
        <div class="filter-group">
          <label>{{ t('report.to') }} : </label>
          <input 
            type="date" 
            v-model="reportsStore.filters.toDate" 
            @change="applyFilters"
          >
        </div>
        <div class="filter-group">
          <label>{{ t('report.type') }} : </label>
          <select v-model="reportsStore.filters.type" @change="applyFilters">
            <option value="all">{{ t('report.all') }}</option>
            <option value="add">{{ t('report.deposits') }}</option>
            <option value="subtract">{{ t('report.withdrawals') }}</option>
          </select>
        </div>
      </div>
      
      <!-- Estadísticas -->
      <div class="stats-cards">
        <div class="stat-card">
          <div class="stat-title">{{ t('report.total_deposits') }}</div>
          <div class="stat-value">{{ currency_prefix }} {{ stats.total_deposits || 0 }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-title">{{ t('report.total_withdrawals') }}</div>
          <div class="stat-value">{{ currency_prefix }} {{ stats.total_withdrawals || 0 }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-title">{{ t('report.total_net') }}</div>
          <div class="stat-value">{{ currency_prefix }} {{ stats.net_balance || 0 }}</div>
        </div>
      </div>
      
      <!-- Tabla de transacciones -->
      <div v-if="!isLoading && transactionsData.length > 0" class="transactions-table">
        <table>
          <thead>
            <tr>
              <th>{{ t('report.date') }}</th>
              <th>{{ t('report.type') }}</th>
              <th>{{ t('report.amount') }}</th>
              <th>{{ t('report.description') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="transaction in transactionsData" :key="transaction.id">
              <td>{{ formatDate(transaction.created_at) }}</td>
              <td>
                <span :class="['type-badge', transaction.type === 'add' ? 'deposit' : 'withdraw']">
                  {{ transaction.type === 'add' ? 'Depósito' : 'Retiro' }}
                </span>
              </td>
              <td :class="transaction.type === 'add' ? 'amount-positive' : 'amount-negative'">
                {{ transaction.type === 'add' ? '+' : '-' }} {{ currency_prefix }} {{ transaction.amount }}
              </td>
              <td>{{ transaction.description || 'N/A' }}</td>
            </tr>
          </tbody>
        </table>
        
        <!-- Paginación -->
        <div class="pagination" v-if="reportsStore.cashReports.transactions.last_page > 1">
          <button 
            :disabled="reportsStore.cashReports.transactions.current_page === 1" 
            @click="changePage(reportsStore.cashReports.transactions.current_page - 1)"
          >
            {{ t('report.previous') }}
          </button>
          <span>{{ t('report.page') }} {{ reportsStore.cashReports.transactions.current_page }} {{ t('report.of') }} {{ reportsStore.cashReports.transactions.last_page }}</span>
          <button 
            :disabled="reportsStore.cashReports.transactions.current_page === reportsStore.cashReports.transactions.last_page" 
            @click="changePage(reportsStore.cashReports.transactions.current_page + 1)"
          >
            {{ t('report.next') }}
          </button>
        </div>
      </div>
      
      <!-- Estado de carga -->
      <div v-else-if="isLoading" class="loading-state">
        <div class="spinner"></div>
        <p>{{ t('report.loading_transactions') }}</p>
      </div>
      
      <!-- Sin datos -->
      <div v-else class="empty-state">
        <p>{{ t('report.no_transactions_found_for_this_period') }}</p>
      </div>
    </div>
    
    <!-- Contenido de la pestaña de estadísticas de juego -->
<div v-if="activeTab === 'games'" class="tab-content">
  <!-- Filtros para estadísticas de juego -->
  <div class="filters">
    <div class="filter-group">
      <label>{{ t('report.from') }} : </label>
      <input 
        type="date" 
        v-model="reportsStore.filters.fromDate" 
        @change="applyFilters"
      >
    </div>
    <div class="filter-group">
      <label>{{ t('report.to') }} : </label>
      <input 
        type="date" 
        v-model="reportsStore.filters.toDate" 
        @change="applyFilters"
      >
    </div>
    <div class="filter-group">
      <label>{{ t('report.game') }} : </label>
      <input 
        type="text" 
        v-model="reportsStore.filters.game"
        placeholder="Search game"
        @change="applyFilters"
      >
    </div>
  </div>
  
  <!-- Resumen estadístico -->
  <div class="stats-cards">
    <div class="stat-card">
      <div class="stat-title">{{ t('report.total_bet') }}</div>
      <div class="stat-value">{{ currency_prefix }} {{ gameTotals.total_bet || 0 }}</div>
    </div>
    <div class="stat-card">
      <div class="stat-title">{{ t('report.total_win') }}</div>
      <div class="stat-value">{{ currency_prefix }} {{ gameTotals.total_win || 0 }}</div>
    </div>
    <div class="stat-card">
      <div class="stat-title">{{ t('report.net_result') }}</div>
      <div class="stat-value" :class="gameTotals.net_result >= 0 ? 'amount-positive' : 'amount-negative'">
        {{ currency_prefix }} {{ gameTotals.net_result || 0 }}
      </div>
    </div>
  </div>
  
  <!-- Contenido de la pestaña de estadísticas de juego -->
<div v-if="activeTab === 'games'" class="tab-content">
  <!-- Filtros para estadísticas de juego - SOLO FECHAS -->
  <div class="filters">
    <div class="filter-group">
      <label>{{ t('report.from') }} : </label>
      <input 
        type="date" 
        v-model="reportsStore.filters.fromDate" 
        @change="applyFilters"
      >
    </div>
    <div class="filter-group">
      <label>{{ t('report.to') }} : </label>
      <input 
        type="date" 
        v-model="reportsStore.filters.toDate" 
        @change="applyFilters"
      >
    </div>
  </div>
  
  <!-- Resumen estadístico -->
  <div class="stats-cards">
    <div class="stat-card">
      <div class="stat-title">{{ t('report.total_bet') }}</div>
      <div class="stat-value">{{ currency_prefix }} {{ gameTotals.total_bet || 0 }}</div>
    </div>
    <div class="stat-card">
      <div class="stat-title">{{ t('report.total_win') }}</div>
      <div class="stat-value">{{ currency_prefix }} {{ gameTotals.total_win || 0 }}</div>
    </div>
    <div class="stat-card">
      <div class="stat-title">{{ t('report.net_result') }}</div>
      <div class="stat-value" :class="(gameTotals.net_result || 0) >= 0 ? 'amount-positive' : 'amount-negative'">
        {{ currency_prefix }} {{ Math.abs(gameTotals.net_result || 0) }}
      </div>
    </div>
  </div>
  
  <!-- Estado de carga para estadísticas de juego -->
  <div v-if="isGameStatsLoading" class="loading-state">
    <div class="spinner"></div>
    <p>{{ t('report.loading_game_history') }}</p>
  </div>
  
  <!-- Sin datos para estadísticas de juego -->
  <div v-else-if="!gameHistory || gameHistory.length === 0" class="no-data-container">
    <div class="empty-state">
      <p>{{ t('report.no_game_data_found') }}</p>
      <p class="empty-state-suggestion">{{ t('report.try_different_date_range') }}</p>
    </div>
  </div>
  
  <!-- Tabla de historial de juego (como en Player History) -->
  <div v-else class="game-history-table">
    <table>
      <thead>
        <tr>
          <th>{{ t('report.date') }}</th>
          <th>{{ t('report.balance_before') }}</th>
          <th>{{ t('report.bet_amount') }}</th>
          <th>{{ t('report.won_loss') }}</th>
          <th>{{ t('report.balance_after') }}</th>
          <th>{{ t('report.bonus_type') }}</th>
          <th>{{ t('report.game_name') }}</th>
          <th>{{ t('report.provider') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="game in gameHistory" :key="game.id">
          <td>{{ formatDate(game.created_at) }}</td>
          <td>{{ game.balance_before }}</td>
          <td>{{ game.balance_bet }}</td>
          <td :class="game.balance_win > 0 ? 'amount-positive' : 'amount-negative'">
            {{ game.balance_win }}
          </td>
          <td>{{ game.balance_after }}</td>
          <td>
            <span v-if="game.cash_back_transaction_id">
              <span v-if="game.cashback_status === 'collected'" class="text-collected">
                {{ t('report.bonus_cashback') }}
              </span>
              <span v-else-if="game.cashback_status === 'pending'" class="text-pending">
                {{ t('report.bonus_pending') }}
              </span>
              <span v-else-if="game.cashback_status === 'cancelled'" class="text-cancelled">
                {{ t('report.bonus_cancelled') }}
              </span>
            </span>
            <span v-else-if="game.jackpot_transaction_id" class="text-jackpot">
              {{ t('report.bonus_jackpot') }}
            </span>
            <span v-else-if="game.is_bonus_bet" class="text-bonus-active">
              {{ t('report.bonus_active') }}
            </span>
            <span v-else>-</span>
          </td>
          <td>{{ game.game }}</td>
          <td>{{ game.provider }}</td>
        </tr>
      </tbody>
    </table>
    
    <!-- Paginación -->
    <div class="pagination" v-if="gameHistoryPagination && gameHistoryPagination.last_page > 1">
      <button 
        :disabled="gameHistoryPagination.current_page === 1" 
        @click="changePage(gameHistoryPagination.current_page - 1)"
      >
        {{ t('report.previous') }}
      </button>
      <span>{{ t('report.page') }} {{ gameHistoryPagination.current_page }} {{ t('report.of') }} {{ gameHistoryPagination.last_page }}</span>
      <button 
        :disabled="gameHistoryPagination.current_page === gameHistoryPagination.last_page" 
        @click="changePage(gameHistoryPagination.current_page + 1)"
      >
        {{ t('report.next') }}
      </button>
    </div>
  </div>
</div>
  </div>
  <!-- Contenido de la pestaña de bonos -->
<div v-if="activeTab === 'bonuses'" class="tab-content">
  <!-- Filtros para bonos -->
  <div class="filters">
    <div class="filter-group">
      <label>{{ t('report.from') }} : </label>
      <input 
        type="date" 
        v-model="reportsStore.filters.fromDate" 
        @change="applyFilters"
      >
    </div>
    <div class="filter-group">
      <label>{{ t('report.to') }} : </label>
      <input 
        type="date" 
        v-model="reportsStore.filters.toDate" 
        @change="applyFilters"
      >
    </div>
    <div class="filter-group">
      <label>{{ t('report.status') }} : </label>
      <select v-model="reportsStore.filters.status" @change="applyFilters">
        <option value="">{{ t('report.all_statuses') }}</option>
        <option value="collected">{{ t('report.collected') }}</option>
        <option value="pending">{{ t('report.pending') }}</option>
        <option value="cancelled">{{ t('report.cancelled') }}</option>
      </select>
    </div>
  </div>
  
  <!-- Estado de carga para estadísticas de bonos -->
  <div v-if="isBonusStatsLoading" class="loading-state">
    <div class="spinner"></div>
    <p>{{ t('report.loading_bonus_stats') }}</p>
  </div>
  
  <!-- Sin datos para estadísticas de bonos -->
  <div v-else-if="!cashbackHistory.length && !jackpotHistory.length" class="no-data-container">
    <div class="empty-state">
      <p>{{ t('report.no_bonus_data_found') }}</p>
      <p class="empty-state-suggestion">{{ t('report.try_different_date_range') }}</p>
    </div>
  </div>
  
  <!-- Si hay datos, mostrar resumen y tablas -->
  <div v-else>
    <!-- Estadísticas de resumen -->
    <div class="stats-cards">
      <div class="stat-card">
        <div class="stat-title">{{ t('report.total_cashback') }}</div>
        <div class="stat-value">{{ currency_prefix }} {{ bonusTotals.total_cashback || 0 }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-title">{{ t('report.total_jackpot') }}</div>
        <div class="stat-value">{{ currency_prefix }} {{ bonusTotals.total_jackpot || 0 }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-title">{{ t('report.total_bonus') }}</div>
        <div class="stat-value">{{ currency_prefix }} {{ bonusTotals.total_bonus || 0 }}</div>
      </div>
    </div>
    
    <!-- Estadísticas por estado -->
    <div class="section-container">
      <h3>{{ t('report.bonus_by_status') }}</h3>
      <div class="status-stats-grid">
        <div 
          v-for="stat in bonusStatusStats" 
          :key="stat.status" 
          class="status-card"
          :class="'status-' + stat.status"
        >
          <div class="status-name">{{ t('report.' + stat.status) }}</div>
          <div class="status-count">{{ stat.count }} {{ t('report.bonuses') }}</div>
          <div class="status-amount">{{ currency_prefix }} {{ stat.total_amount }}</div>
        </div>
      </div>
    </div>
    
    <!-- Historial de Cashback -->
    <div v-if="cashbackHistory.length > 0" class="section-container">
      <h3>{{ t('report.cashback_history') }}</h3>
      <div class="bonus-table">
        <table>
          <thead>
            <tr>
              <th>{{ t('report.date') }}</th>
              <th>{{ t('report.bonus_amount') }}</th>
              <th>{{ t('report.status') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="bonus in cashbackHistory" :key="bonus.transaction_id">
              <td>{{ formatDate(bonus.created_at) }}</td>
              <td>{{ currency_prefix }} {{ bonus.bonus_amount }}</td>
              <td>
                <span :class="'status-badge status-' + bonus.status">
                  {{ t('report.' + bonus.status) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    
    <!-- Historial de Jackpot -->
    <div v-if="jackpotHistory.length > 0" class="section-container">
      <h3>{{ t('report.jackpot_history') }}</h3>
      <div class="bonus-table">
        <table>
          <thead>
            <tr>
              <th>{{ t('report.date') }}</th>
              <th>{{ t('report.jackpot_name') }}</th>
              <th>{{ t('report.level') }}</th>
              <th>{{ t('report.amount') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="jackpot in jackpotHistory" :key="jackpot.transaction_id">
              <td>{{ formatDate(jackpot.created_at) }}</td>
              <td>{{ jackpot.jackpot_name }}</td>
              <td>{{ jackpot.level }}</td>
              <td>{{ currency_prefix }} {{ jackpot.amount }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</div>

</div>
</template>

<style scoped>
/* Estilos básicos del modal */
.player-reports-modal {
  background-color: #1a1a2e;
 
  border-radius: 10px;
  color: white;
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
  margin: 0 auto;
  padding: 20px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #2a2a5a;
  padding-bottom: 15px;
  margin-bottom: 20px;
}

.close-modal-btn {
  background: none;
  border: none;
  color: #ffffff;
  font-size: 24px;
  cursor: pointer;
}

/* Información del jugador */
.player-info {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
}

.info-item {
  background-color: #16213e;
  padding: 8px 15px;
  border-radius: 5px;
  flex: 1;
  min-width: 150px;
}

.info-label {
  color: #4895ef;
  margin-right: 8px;
}

/* Navegación por pestañas */
.reports-tabs {
  display: flex;
  gap: 5px;
  border-bottom: 1px solid #2a2a5a;
  margin-bottom: 20px;
}

.tab-btn {
  background: none;
  border: none;
  color: white;
  padding: 10px 20px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.tab-btn.active {
  color: #4895ef;
  border-bottom: 2px solid #4895ef;
}

.tab-content {
  padding: 10px 0;
}

/* Filtros */
.filters {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.filter-group label {
  font-size: 12px;
  color: #6c7293;
}

.filter-group input, .filter-group select {
  background-color: #16213e;
  border: 1px solid #2a2a5a;
  color: white;
  padding: 8px;
  border-radius: 4px;
}

/* Tarjetas de estadísticas */
.stats-cards {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.stat-card {
  background-color: #16213e;
  border-radius: 6px;
  padding: 15px;
  flex: 1;
  min-width: 150px;
}

.stat-title {
  font-size: 14px;
  color: #6c7293;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 18px;
  font-weight: bold;
}

/* Estilos para tablas */
.transactions-table, .game-history-table, .bonus-table {
  overflow-x: auto;
  margin-bottom: 15px;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th, td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #2a2a5a;
}

th {
  background-color: #16213e;
  color: #6c7293;
}

/* Badges e indicadores */
.type-badge, .status-badge {
  display: inline-block;
  padding: 5px 10px;
  border-radius: 20px;
  font-size: 12px;
}

.type-badge.deposit {
  background-color: #04751e;
}

.type-badge.withdraw {
  background-color: #8c0311;
}

.status-badge.status-collected {
  background-color: #28a745;
  color: white;
}

.status-badge.status-pending {
  background-color: #ffc107;
  color: black;
}

.status-badge.status-cancelled {
  background-color: #dc3545;
  color: white;
}

/* Colores para valores positivos/negativos */
.amount-positive {
  color: #28a745;
}

.amount-negative {
  color: #dc3545;
}

/* Paginación */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
}

.pagination button {
  background-color: #16213e;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
}

.pagination button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Estados de carga y vacío */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #6c7293;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid #4895ef;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.no-data-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

.empty-state {
  text-align: center;
  padding: 30px;
  background-color: #1a1a2e;
  border-radius: 8px;
  max-width: 80%;
}

.empty-state p {
  color: #6c7293;
  margin-bottom: 8px;
}

.empty-state-suggestion {
  font-size: 0.9em;
  opacity: 0.8;
}

/* Sección de contenedores */
.section-container {
  margin-top: 20px;
  background-color: #16213e;
  border-radius: 8px;
  padding: 15px;
}

.section-container h3 {
  margin-top: 0;
  margin-bottom: 15px;
  color: #4895ef;
  font-size: 16px;
}

/* Estadísticas de estado para bonos */
.status-stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.status-card {
  background-color: #16213e;
  border-radius: 8px;
  padding: 15px;
  text-align: center;
}

.status-collected {
  border-left: 4px solid #28a745;
}

.status-pending {
  border-left: 4px solid #ffc107;
}

.status-cancelled {
  border-left: 4px solid #dc3545;
}

.status-name {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 5px;
}

.status-count {
  color: #6c7293;
  margin-bottom: 5px;
  font-size: 14px;
}

.status-amount {
  font-size: 18px;
  font-weight: bold;
}

/* Botón de reportes */
.reports-button {
  background-color: #4895ef;
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 10px;
}
.text-collected{
  color:#128101;
}
.text-pending{
  color:#ffc107;
}
.text-cancelled{
  color:#dc3545;
}
.text-jackpot{
  color:darkmagenta;
}
.text-bonus-active{
  color:#4895ef;
}
/* Media queries para responsividad */
@media (max-width: 768px) {
  .player-reports-modal {
    width: 95%;
    padding: 15px;
  }
  
  .filters {
    flex-direction: column;
  }
  
  .info-item, .stat-card {
    min-width: 100%;
  }
  
  .reports-tabs {
    overflow-x: auto;
  }
  
  .tab-btn {
    white-space: nowrap;
    padding: 10px 15px;
  }
  
  th, td {
    padding: 8px;
    font-size: 14px;
  }
}

/* Para pantallas muy pequeñas */
@media (max-width: 480px) {
  .player-reports-modal {
    padding: 10px;
  }
  
  .modal-header h2 {
    font-size: 18px;
  }
  
  .stat-value {
    font-size: 16px;
  }
  
  .status-stats-grid {
    grid-template-columns: 1fr;
  }
  
  .pagination {
    flex-direction: column;
    gap: 5px;
  }
}
</style>